import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import './InquiryPopup.css';

const STORAGE_KEY = 'khp_inquiry_submitted';

export function hasInquiryBeenSubmitted() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setInquirySubmitted() {
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch {}
}

function InquiryPopup({
  enabled,
  initialDelaySeconds = 8,
  incrementSeconds = 2,
  onSubmitted
}) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    subject: '',
    comment: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const nextShowDelayRef = useRef(initialDelaySeconds * 1000);
  const timerRef = useRef(null);

  const scheduleNext = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!enabled || hasInquiryBeenSubmitted()) return;
    const delay = nextShowDelayRef.current;
    timerRef.current = setTimeout(() => {
      setOpen(true);
      nextShowDelayRef.current += incrementSeconds * 1000;
    }, delay);
  }, [enabled, incrementSeconds]);

  useEffect(() => {
    if (!enabled || hasInquiryBeenSubmitted()) return;
    nextShowDelayRef.current = initialDelaySeconds * 1000;
    scheduleNext();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, initialDelaySeconds, scheduleNext]);

  const handleClose = () => {
    setOpen(false);
    setError('');
    scheduleNext();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await api.post('/inquiries', { inquiry: formData });
      setSubmitted(true);
      setInquirySubmitted();
      onSubmitted?.();
      setTimeout(() => {
        setOpen(false);
        setSubmitted(false);
        setFormData({ full_name: '', email: '', subject: '', comment: '' });
      }, 2000);
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  if (!enabled) return null;

  return (
    <>
      <div className={`inquiry-popup-backdrop ${open ? 'inquiry-popup-backdrop--open' : ''}`} onClick={handleClose} aria-hidden="true" />
      <div className={`inquiry-popup ${open ? 'inquiry-popup--open' : ''}`} role="dialog" aria-modal="true" aria-labelledby="inquiry-popup-title">
        <div className="inquiry-popup-inner" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="inquiry-popup-close" onClick={handleClose} aria-label="Close">
            ×
          </button>
          <h2 id="inquiry-popup-title" className="inquiry-popup-title">Get in Touch</h2>
          <p className="inquiry-popup-sub">Our team will contact you within 24 hours.</p>
          {submitted ? (
            <p className="inquiry-popup-success">Thank you! Your inquiry has been submitted.</p>
          ) : (
            <form onSubmit={handleSubmit} className="inquiry-popup-form">
              <div className="inquiry-popup-field">
                <label htmlFor="popup_full_name">Full Name</label>
                <input id="popup_full_name" type="text" name="full_name" value={formData.full_name} onChange={handleChange} required />
              </div>
              <div className="inquiry-popup-field">
                <label htmlFor="popup_email">Email</label>
                <input id="popup_email" type="email" name="email" value={formData.email} onChange={handleChange} required />
              </div>
              <div className="inquiry-popup-field">
                <label htmlFor="popup_subject">Subject</label>
                <input id="popup_subject" type="text" name="subject" value={formData.subject} onChange={handleChange} required />
              </div>
              <div className="inquiry-popup-field">
                <label htmlFor="popup_comment">Message</label>
                <textarea id="popup_comment" name="comment" value={formData.comment} onChange={handleChange} rows="4" required />
              </div>
              {error && <div className="inquiry-popup-error">{error}</div>}
              <div className="inquiry-popup-actions">
                <button type="submit" className="inquiry-popup-btn-submit" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Submit'}
                </button>
                <button type="button" className="inquiry-popup-btn-cancel" onClick={handleClose}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}

export default InquiryPopup;
